import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import classNames from "classnames";
import { useTranslation } from "next-i18next";

import { getMainAppHost } from "@/shared/utils/url/url";
import { parseHashInitAction, URLInitAction } from "@/shared/utils/init-actions";
import { useFullScreen } from "@/hooks/index";
import { mainTreeActions, systemActions } from "@/store/actions";
import { useModalContext } from "@/shared/contexts/modal";
import Breadcrumbs from "@/components/Breadcrumbs/Breadcrumbs";
import Container from "@/components/Container/Container";
import Footer from "@/components/Footer/Footer";
import Header from "@/components/Header/Header";
import CookiesNotification from "../cookiesNotification/CookiesNotification";
import HeaderLanding from "@/components/Header/HeaderLanding";
import { NEXT_URLS } from "@/utils/routes/routes.constants";
import { useAudioContext } from "@/shared/contexts/AudioContext";
import { usePagePropsContext } from "@/contexts/pageProps";
import { getBookIdNumber } from "@/shared/utils/content/content";
import { tocActions } from "@/store/actions/toc.actions";
import { selectorTocItems } from "@/shared/store/toc/selectors";
import { getReaderAudioChapter } from "@/shared/utils/reader/reader";
import { GlobalSwipeableProvider } from "@/contexts/swipeable";
import { useSelectorBook } from "@/shared/store/mainTree/hooks-selectors";
import PortalMainAfter from "@/shared/components/Portal/PortalMainAfter/PortalMainAfter";
import TopPanelLoader from "../TopPanel/TopPanelLoader";

import styles from "./main-layout.module.scss";

const AdvSearchView = dynamic(() => import("@/components/advSearch/AdvSearchView"), {
  ssr: false,
});

const SmallPlayer = dynamic(() => import("../Player/SmallPlayer"), {
  ssr: false,
});

// dynamic, to prevent a console error: Hydration failed due to the UI mismatch…
const TopPanel = dynamic(() => import("../TopPanel/TopPanel"), {
  ssr: false,
  loading: TopPanelLoader,
});

const MainLayout = ({
  children,
  breadcrumbs,
  afterBreadcrumbsSlot,
  contentWrapperTag,
  isLoading,
  fullVersionLinkCustom,
  isVisibleFooter = true,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const router = useRouter();
  const { isLanding, isBigLanding } = usePagePropsContext();
  const { setFullScreen, disableFullScreen } = useFullScreen();
  const [isVisibleLoader, setVisibleLoader] = useState(isLoading);
  const { playChapterAndShowPlayer, chapterId } = useAudioContext();
  const { showDialog, onClose } = useModalContext();

  const showAdvSearchPanel = useSelector((state) => state.system.showAdvSearchPanel);

  const fullVersionLink = fullVersionLinkCustom || getMainAppHost() + router.asPath;
  const isRouteHome = router.route === NEXT_URLS.home;
  const isReaderPage = router.asPath.includes(NEXT_URLS.read);
  const isLandingReaderPage = isLanding && isReaderPage;

  const isTopPanelShown = breadcrumbs || afterBreadcrumbsSlot;

  const initActionURL = useMemo(() => {
    return parseHashInitAction(router.asPath);
  }, [router.asPath]);

  let idInitActionURL, nameActionInitActionURL, positionInitActionURL;
  if (initActionURL) {
    idInitActionURL = initActionURL.id;
    nameActionInitActionURL = initActionURL.action;
    positionInitActionURL = initActionURL.position;
  }

  const idOriginBookInitAction = idInitActionURL ? getBookIdNumber(idInitActionURL) : undefined;

  const bookInitAction = useSelectorBook(idOriginBookInitAction);
  const tocItems = useSelector(selectorTocItems);
  const tocInitAction = idOriginBookInitAction ? tocItems[idOriginBookInitAction] : null;

  // Fetches the toc of the init action book.
  useEffect(() => {
    if (idOriginBookInitAction) {
      dispatch(tocActions.fetch(idOriginBookInitAction));
    }
  }, [idOriginBookInitAction]);

  // Fetches the book of the init action.
  useEffect(() => {
    if (idOriginBookInitAction) {
      dispatch(mainTreeActions.fetchBook(idOriginBookInitAction));
    }
  }, [idOriginBookInitAction]);

  // Does a URL init action if the url has one.
  useEffect(() => {
    if (!idInitActionURL || !bookInitAction || !tocInitAction) {
      return undefined;
    }

    let timeout;

    if (nameActionInitActionURL === URLInitAction.listen) {
      timeout = setTimeout(() => {
        playChapterAndShowPlayer(idInitActionURL, positionInitActionURL, {
          fromML: true,
          onError: () => {
            const { title } = bookInitAction;
            const chapterSuitable = getReaderAudioChapter(tocInitAction, idInitActionURL);

            showDialog(
              t("doYouWannaPlay", { title }),
              chapterSuitable.title || chapterSuitable.refcode_short,
              () => {
                playChapterAndShowPlayer(idInitActionURL, positionInitActionURL, {
                  fromML: true,
                });
                onClose();
              },
              {
                positiveBtn: t("yes"),
                negativeBtn: t("no"),
                noCancelable: true,
                onCancel: onClose,
              },
            );
          },
        });
      }, 0);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [bookInitAction, tocInitAction]);

  // Regulates full screen for some cases.
  useEffect(() => {
    if (!isLanding || isLandingReaderPage) {
      setFullScreen();
    } else {
      disableFullScreen();
    }
  }, [isLanding, isLandingReaderPage, setFullScreen]);

  // Regulates the <body/> class names "is-home" and "is-reader".
  useEffect(() => {
    if (isRouteHome) {
      global.window.document.body.classList.add("is-home");
    } else {
      global.window.document.body.classList.remove("is-home");
    }

    if (isReaderPage) {
      global.window.document.documentElement.classList.add("is-reader");
    } else {
      global.window.document.documentElement.classList.remove("is-reader");
    }
  }, [isReaderPage, isRouteHome]);

  // Delays the loader for little time loading processes.
  useEffect(() => {
    let timeout;
    if (isLoading) {
      timeout = setTimeout(() => {
        setVisibleLoader(true);
      }, 200);
    } else {
      setVisibleLoader(false);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [isLoading]);

  const ContentWrapper = contentWrapperTag || Container;

  const header = isLanding ? <HeaderLanding /> : <Header fullVersionLink={fullVersionLink} />;

  return (
    <>
      {header}
      <GlobalSwipeableProvider tag="main" className={styles["main-container"]}>
        <CookiesNotification />

        {showAdvSearchPanel && (
          <AdvSearchView onClose={() => dispatch(systemActions.setShowAdvSearchPanel(false))} />
        )}

        <ContentWrapper
          className={classNames("content-wrap", styles["content-wrap"], {
            hidden: showAdvSearchPanel,
            [styles["big-land-main"]]: isBigLanding,
          })}>
          {isTopPanelShown && (
            <TopPanel>
              {breadcrumbs && <Breadcrumbs {...breadcrumbs} isLanding={isLanding} />}
              {afterBreadcrumbsSlot}
            </TopPanel>
          )}
          {children}
        </ContentWrapper>
      </GlobalSwipeableProvider>

      <PortalMainAfter />

      {isVisibleLoader && <div className={styles["loader-popup"]}>{t("loading")}…</div>}
      {!(isLandingReaderPage || isReaderPage) && isVisibleFooter && (
        <Footer
          className={classNames(styles["footer-container"], { hidden: showAdvSearchPanel })}
        />
      )}
      {chapterId && <SmallPlayer />}
    </>
  );
};

MainLayout.propTypes = {
  isLoading: PropTypes.bool,
  contentWrapperTag: PropTypes.any,
  fullVersionLinkCustom: PropTypes.string,
  breadcrumbs: PropTypes.object,
  children: PropTypes.node,
  afterBreadcrumbsSlot: PropTypes.node,
  bookTitles: PropTypes.array,
  isVisibleFooter: PropTypes.bool,
};

export default MainLayout;
